import React from 'react';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { NotSubscribedIcon, SubscribedIcon } from '@common/utils/icons/icons';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectUser } from '../../store/general/general-slice-selectors';
import RobotsTable from '../robots/RobotsTable';
import { selectOrganizationsData } from '../../store/organization/organization-slice-selectors';
import Chip from '@mui/material/Chip';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { replaceArgsInMessage } from '../../utils/strings';
import { postSubscribeToOrganizationById } from '../../store/general/general-actions';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { resetUserRestStatus } from '../../store/general/general-slice';
import Tooltip from '@mui/material/Tooltip';
import { selectRobotsData } from '../../store/robots/robots-slice-selectors';
import Typography from '@mui/material/Typography';

const OrganizationDashboard: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => selectUser(state));
  const organizationData = useAppSelector((state) => selectOrganizationsData(state));
  const robotsData = useAppSelector((state) => selectRobotsData(state));

  const toggleSubscribeClickHandler = () => {
    if (organizationData.current?.id != null) {
      dispatch(postSubscribeToOrganizationById(organizationData.current.id));
    }
  };

  const closeSubscribeSnackbarHandler = () => {
    dispatch(
      resetUserRestStatus({
        postSubscribeOrganizationStatus: HttpFetchStatus.Idle,
        postSubscribeOrganizationError: null,
      }),
    );
  };

  const isSubscribed = user.data.organizationSubscriptions.some(
    (org) => org.id === organizationData.current?.id,
  );

  return (
    <React.Fragment>
      <Stack spacing={{ xs: 1, sm: 2 }} p={{ xs: 1, sm: 2 }}>
        <Typography variant="h2" fontSize={24} lineHeight={2} sx={{ display: { xs: 'none', sm: 'block' } }}>
          {organizationData.current?.name}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
          {organizationData.current?.role != null && (
            <Chip
              label={t('organization-role-' + organizationData.current.role)}
              variant="outlined"
            />
          )}
          <Tooltip title={t('info-tooltip-robot-count')}>
            <Chip
              label={`${robotsData.pagination?.count ?? 0} ${t('table-header-robot-count').toLocaleLowerCase()}`}
              variant="outlined"
            />
          </Tooltip>
          <TooltipIconButton
            tooltip={replaceArgsInMessage(
              isSubscribed ? t('button-subscribed') : t('button-not-subscribed'),
              [organizationData.current?.name ?? ''],
            )}
            onClick={toggleSubscribeClickHandler}
            icon={isSubscribed ? <SubscribedIcon /> : <NotSubscribedIcon />}
            color="primary"
          />
        </Stack>
        <RobotsTable />
      </Stack>

      <CustomSnackbar
        open={user.rest.postSubscribeOrganizationStatus === HttpFetchStatus.Success}
        title={replaceArgsInMessage(
          isSubscribed ? t('success-subscribing') : t('success-unsubscribing'),
          [organizationData.current?.name ?? ''],
        )}
        onClose={closeSubscribeSnackbarHandler}
      />
      <CustomSnackbar
        open={user.rest.postSubscribeOrganizationStatus === HttpFetchStatus.Failed}
        title={replaceArgsInMessage(
          isSubscribed ? t('error-subscribing') : t('error-unsubscribing'),
          [organizationData.current?.name ?? ''],
        )}
        onClose={closeSubscribeSnackbarHandler}
        severity="error"
      />
    </React.Fragment>
  );
};

export default OrganizationDashboard;
